/**
 * F40 (Wave 2 Track D — admin) — promotions moderation list.
 *
 * Route: `/promotions`
 * Permission: `promotions:read` (super_admin + finance + support +
 *             content_moderator). The in-page row link opens the detail page,
 *             whose suspend/restore CTAs are additionally gated on
 *             `promotions:moderate`.
 *
 * A Chip-row status filter (All / Active / Paused / Scheduled / Suspended /
 * Expired / Cancelled) + a display-mode <Select> filter + a venue <SearchInput>
 * + a paginated <Table> (code, venue, discount, display mode Badge, status
 * Badge, a "Suspended" Badge when `suspendedAt != null`, validity, redemptions
 * count). Rows link to the detail page.
 *
 * The "Suspended" status chip is SYNTHETIC: suspension is orthogonal to the
 * lifecycle `status` (it sets `suspendedAt`), so picking the "Suspended" tab
 * issues a `suspended=true` server filter rather than a status filter.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Chip,
  EmptyState,
  Pagination,
  PromoStatusBadge,
  SearchInput,
  Select,
  Skeleton,
  Table,
  type TableColumn,
  useDebouncedValue,
} from '@bravemove/ui-web';
import {
  PROMOTION_DISPLAY_MODES,
  type PromotionDisplayMode,
  type PromotionStatus,
} from '@bravemove/types';
import {
  adminPromotionsApi,
  type AdminPromotionRow,
  type ListPromotionsOpts,
} from '../../api/promotions';
import { translateApiError } from '../../auth/errors';
import { PageHeader } from '../../components/PageHeader';

const PAGE_SIZE = 50;

/**
 * UI-side status filter. `'suspended'` is a SYNTHETIC value (suspension is
 * orthogonal to the lifecycle status), so it maps to `suspended=true` on the
 * server fetch rather than a `status=` filter.
 */
type StatusFilter = 'all' | PromotionStatus | 'suspended';

const STATUS_TABS: ReadonlyArray<{ value: StatusFilter; sharedKey?: string; fallback: string }> = [
  { value: 'all', fallback: 'All' },
  { value: 'active', sharedKey: 'promotion:status.active', fallback: 'Active' },
  { value: 'paused', sharedKey: 'promotion:status.paused', fallback: 'Paused' },
  { value: 'scheduled', sharedKey: 'promotion:status.scheduled', fallback: 'Scheduled' },
  { value: 'suspended', fallback: 'Suspended' },
  { value: 'expired', sharedKey: 'promotion:status.expired', fallback: 'Expired' },
  { value: 'cancelled', sharedKey: 'promotion:status.cancelled', fallback: 'Cancelled' },
];

function formatDiscount(row: AdminPromotionRow, locale: string): string {
  if (row.discountType === 'percent') {
    return `${(row.discountValue / 100).toLocaleString(locale)}%`;
  }
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: row.discountCurrency,
    }).format(row.discountValue / 100);
  } catch {
    return `${(row.discountValue / 100).toFixed(2)} ${row.discountCurrency}`;
  }
}

function formatDay(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function AdminPromotionsListPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const statusParam = (searchParams.get('status') as StatusFilter | null) ?? 'all';
  const displayModeParam =
    (searchParams.get('displayMode') as PromotionDisplayMode | 'all' | null) ?? 'all';
  const venueParam = searchParams.get('venueId') ?? '';
  // F61 (A4) — filter once typing pauses, not per keystroke.
  const debouncedVenue = useDebouncedValue(venueParam, 300);
  const pageParam = Number(searchParams.get('page') ?? '1') || 1;

  const [venueInput, setVenueInput] = useState(venueParam);
  const [rows, setRows] = useState<readonly AdminPromotionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const opts: ListPromotionsOpts = {
        page: pageParam,
        perPage: PAGE_SIZE,
        ...(displayModeParam !== 'all' ? { displayMode: displayModeParam } : {}),
        ...(debouncedVenue ? { venueId: debouncedVenue } : {}),
        ...(statusParam === 'suspended'
          ? { suspended: true }
          : statusParam !== 'all'
            ? { status: statusParam }
            : {}),
      };
      const data = await adminPromotionsApi.listPromotions(opts);
      setRows(data.data);
      setTotal(data.pagination.total);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [statusParam, displayModeParam, debouncedVenue, pageParam, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList();
  }, [fetchList]);

  const setParam = (key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value && value !== 'all') next.set(key, value);
      else next.delete(key);
      next.delete('page');
      return next;
    });
  };

  const onPageChange = (next: number) => {
    setSearchParams((prev) => {
      const np = new URLSearchParams(prev);
      np.set('page', String(next));
      return np;
    });
  };

  const onVenueSearch = (value: string) => {
    setVenueInput(value);
    setParam('venueId', value.trim());
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const columns = useMemo<readonly TableColumn<AdminPromotionRow>[]>(
    () => [
      {
        key: 'code',
        header: t('admin:promotion.list.columnCode', { defaultValue: 'Code' }),
        cell: (row) =>
          row.code ? (
            <span className="font-mono text-[12.5px] font-semibold text-ink">{row.code}</span>
          ) : (
            <span className="text-[11.5px] text-ink-3 italic">
              {t('admin:promotion.list.autoNoCode', { defaultValue: 'Auto (no code)' })}
            </span>
          ),
      },
      {
        key: 'venue',
        header: t('admin:promotion.list.columnVenue', { defaultValue: 'Venue' }),
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <span className="text-[12.5px] text-ink">{row.venueName ?? row.venueId}</span>
            <span className="font-mono text-[10.5px] text-ink-3">{row.venueId}</span>
          </div>
        ),
      },
      {
        key: 'discount',
        header: t('admin:promotion.list.columnDiscount', { defaultValue: 'Discount' }),
        cell: (row) => (
          <span className="text-[12.5px] font-semibold text-ink tabular-nums">
            {formatDiscount(row, locale)}
          </span>
        ),
      },
      {
        key: 'displayMode',
        header: t('admin:promotion.list.columnDisplayMode', { defaultValue: 'Display' }),
        cell: (row) => (
          <Badge tone="neutral" size="sm">
            {t(`promotion:displayMode.${row.displayMode}`, { defaultValue: row.displayMode })}
          </Badge>
        ),
      },
      {
        key: 'status',
        header: t('admin:promotion.list.columnStatus', { defaultValue: 'Status' }),
        cell: (row) => (
          <div className="flex items-center gap-1.5">
            <PromoStatusBadge
              size="sm"
              status={row.status}
              label={t(`promotion:status.${row.status}`, { defaultValue: row.status })}
            />
            {row.suspendedAt !== null ? (
              <PromoStatusBadge
                size="sm"
                status="suspended_by_admin"
                label={t('admin:promotion.list.suspendedBadge', { defaultValue: 'Suspended' })}
                data-testid={`promotion-suspended-badge-${row.id}`}
              />
            ) : null}
          </div>
        ),
      },
      {
        key: 'validity',
        header: t('admin:promotion.list.columnValidity', { defaultValue: 'Validity' }),
        cell: (row) => (
          <span className="text-[11.5px] text-ink-2">
            {formatDay(row.validFrom, locale)} – {formatDay(row.validUntil, locale)}
          </span>
        ),
      },
      {
        key: 'redemptions',
        header: t('admin:promotion.list.columnRedemptions', { defaultValue: 'Redemptions' }),
        align: 'end',
        cell: (row) => (
          <span className="text-[12px] text-ink-2 tabular-nums">
            {row.totalUsesCount}
            {row.totalUsesCap !== null ? ` / ${row.totalUsesCap}` : ''}
          </span>
        ),
      },
    ],
    [t, locale],
  );

  return (
    <div className="space-y-6" data-testid="promotions-list-page">
      <PageHeader
        title={t('admin:promotion.list.title', { defaultValue: 'Promotions' })}
        subtitle={t('admin:promotion.list.subtitle', {
          defaultValue:
            'Cross-venue promotions. Filter by status or display mode; search by venue; suspend abusive promotions.',
        })}
      />

      {/* Chip-row status filter */}
      <div
        role="tablist"
        aria-label={t('admin:promotion.list.filterStatus', { defaultValue: 'Status' })}
        className="flex flex-wrap items-center gap-2"
      >
        {STATUS_TABS.map((tab) => (
          <Chip
            key={tab.value}
            selected={statusParam === tab.value}
            onClick={() => setParam('status', tab.value)}
            data-testid={`promotion-status-chip-${tab.value}`}
          >
            {tab.sharedKey
              ? t(tab.sharedKey, { defaultValue: tab.fallback })
              : tab.value === 'all'
                ? t('admin:promotion.list.filterAll', { defaultValue: 'All' })
                : t('admin:promotion.list.suspendedBadge', { defaultValue: 'Suspended' })}
          </Chip>
        ))}
      </div>

      {/* Display-mode + venue filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[200px]">
          <label
            className="block text-[11px] uppercase tracking-wider text-ink-3 mb-1"
            htmlFor="promotion-display-mode-filter"
          >
            {t('admin:promotion.list.filterDisplayMode', { defaultValue: 'Display mode' })}
          </label>
          <Select
            id="promotion-display-mode-filter"
            value={displayModeParam}
            data-testid="promotion-display-mode-filter"
            aria-label={t('admin:promotion.list.filterDisplayMode', {
              defaultValue: 'Display mode',
            })}
            options={[
              {
                value: 'all',
                label: t('admin:promotion.list.filterAll', { defaultValue: 'All' }),
              },
              ...PROMOTION_DISPLAY_MODES.map((m) => ({
                value: m,
                label: t(`promotion:displayMode.${m}`, { defaultValue: m }),
              })),
            ]}
            onChange={(e) => setParam('displayMode', e.target.value)}
          />
        </div>
        <div className="min-w-[240px]">
          <label
            className="block text-[11px] uppercase tracking-wider text-ink-3 mb-1"
            htmlFor="promotion-venue-search"
          >
            {t('admin:promotion.list.venueSearch', { defaultValue: 'Venue id' })}
          </label>
          <SearchInput
            id="promotion-venue-search"
            value={venueInput}
            data-testid="promotion-venue-search"
            placeholder={t('admin:promotion.list.venueSearchPlaceholder', {
              defaultValue: 'Filter by venue id',
            })}
            aria-label={t('admin:promotion.list.venueSearch', { defaultValue: 'Venue id' })}
            onChange={(e) => onVenueSearch(e.target.value)}
          />
        </div>
      </div>

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="promotions-list-loading">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={t('admin:promotion.list.empty', { defaultValue: 'No promotions in this view.' })}
        />
      ) : (
        <>
          <Table
            data={rows as AdminPromotionRow[]}
            columns={columns as TableColumn<AdminPromotionRow>[]}
            rowKey={(row) => row.id}
            onRowPress={(row) => navigate(`/promotions/${row.id}`)}
          />
          {pageCount > 1 ? (
            <div className="flex justify-end">
              <Pagination
                page={pageParam}
                pageCount={pageCount}
                onChange={onPageChange}
                ariaLabel={t('admin:promotion.list.title', { defaultValue: 'Promotions' })}
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export default AdminPromotionsListPage;
